import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Check, UserPlus, KeyRound, Share2 } from "lucide-react";

export default function Onboarding() {
  const { user, couple, refresh } = useAuth();
  const [tab, setTab] = useState("create");
  const [coupleName, setCoupleName] = useState("");
  const [weddingDate, setWeddingDate] = useState("");
  const stage = user?.relationship_stage || "engaged";
  const [code, setCode] = useState("");
  const [createdCouple, setCreatedCouple] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (couple && !createdCouple) {
      nav("/dashboard", { replace: true });
    }
  }, [couple, createdCouple, nav]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/couples/create", {
        couple_name: coupleName || null,
        wedding_date: weddingDate || null,
        relationship_stage: stage,
      });
      setCreatedCouple(data);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/couples/join", { invite_code: code });
      toast.success("Couple rejoint !");
      await refresh();
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(createdCouple.invite_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareCode = async () => {
    const code = createdCouple?.invite_code;
    if (!code) return;
    const url = window.location.origin;
    const text =
      `Rejoins-moi sur Classes Prénuptiales 💛\n` +
      `Crée ton compte sur ${url}, puis saisis le code ${code} pour rejoindre notre parcours de préparation au mariage.`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Classes Prénuptiales", text });
      } catch (e) {
        /* partage annulé */
      }
    } else {
      try {
        await navigator.clipboard.writeText(text);
        toast.success("Message copié — colle-le dans WhatsApp, SMS ou email.");
      } catch (e) {
        toast.error("Impossible de partager sur cet appareil.");
      }
    }
  };

  if (createdCouple) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 md:py-24 text-center animate-fade-in-up">
        <p className="text-xs tracking-[0.3em] uppercase text-[#7A8B76] font-semibold mb-4">
          Couple créé
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mb-4">
          Invitez votre <span className="italic">moitié</span>
        </h1>
        <p className="text-[#5C5754] mb-12 leading-relaxed">
          Partagez ce code unique avec votre partenaire. Il/elle créera un compte
          puis saisira ce code pour vous rejoindre.
        </p>
        <div className="bg-white border border-[#E6E2DD] rounded-3xl p-10 mb-8">
          <p className="text-xs tracking-[0.3em] uppercase text-[#8A8480] mb-4">
            Code d'invitation
          </p>
          <p
            data-testid="invite-code-display"
            className="font-heading text-6xl sm:text-7xl tracking-[0.15em] text-[#C86B5E]"
          >
            {createdCouple.invite_code}
          </p>
          <button
            data-testid="copy-code-button"
            onClick={copyCode}
            className="mt-8 inline-flex items-center gap-2 text-sm text-[#5C5754] hover:text-[#C86B5E] transition-colors"
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? "Copié" : "Copier le code"}
          </button>
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            data-testid="share-code-button"
            onClick={shareCode}
            className="bg-[#C86B5E] hover:bg-[#B35A4E] text-white rounded-full px-10 py-4 transition-colors inline-flex items-center gap-2"
          >
            <Share2 className="w-4 h-4" /> Partager le code
          </button>
          <button
            data-testid="goto-dashboard-after-create"
            onClick={() => nav("/dashboard")}
            className="text-[#5C5754] hover:text-[#C86B5E] rounded-full px-6 py-4 transition-colors"
          >
            Continuer vers le parcours
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-16 md:py-24">
      <p className="text-xs tracking-[0.3em] uppercase text-[#C86B5E] font-semibold mb-4">
        Bienvenue {user?.name}
      </p>
      <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mb-4">
        Reliez votre <span className="italic">couple</span>
      </h1>
      <p className="text-[#5C5754] mb-12 leading-relaxed">
        Choisissez : si vous initiez le parcours, créez un nouveau couple.
        Si votre partenaire a déjà créé le couple, rejoignez-le avec son code.
      </p>

      <div className="grid sm:grid-cols-2 gap-4 mb-10">
        <button
          data-testid="onboarding-tab-create"
          onClick={() => setTab("create")}
          className={`text-left rounded-2xl p-6 border-2 transition-all ${
            tab === "create"
              ? "border-[#C86B5E] bg-[#C86B5E]/5"
              : "border-[#E6E2DD] bg-white hover:border-[#C86B5E]/40"
          }`}
        >
          <div className="w-10 h-10 rounded-full bg-[#C86B5E]/10 flex items-center justify-center mb-3">
            <UserPlus className="w-5 h-5 text-[#C86B5E]" strokeWidth={1.5} />
          </div>
          <p className="font-heading text-xl text-[#2C2A29] mb-1">J'initie le parcours</p>
          <p className="text-sm text-[#8A8480] leading-relaxed">
            Je crée le couple et j'obtiens un code à partager à mon/ma partenaire.
          </p>
        </button>
        <button
          data-testid="onboarding-tab-join"
          onClick={() => setTab("join")}
          className={`text-left rounded-2xl p-6 border-2 transition-all ${
            tab === "join"
              ? "border-[#C86B5E] bg-[#C86B5E]/5"
              : "border-[#E6E2DD] bg-white hover:border-[#C86B5E]/40"
          }`}
        >
          <div className="w-10 h-10 rounded-full bg-[#7A8B76]/15 flex items-center justify-center mb-3">
            <KeyRound className="w-5 h-5 text-[#7A8B76]" strokeWidth={1.5} />
          </div>
          <p className="font-heading text-xl text-[#2C2A29] mb-1">J'ai reçu un code</p>
          <p className="text-sm text-[#8A8480] leading-relaxed">
            Mon/ma partenaire a déjà créé le couple : je le/la rejoins avec son code.
          </p>
        </button>
      </div>

      {tab === "create" ? (
        <form onSubmit={handleCreate} className="space-y-6">
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-[#5C5754] font-semibold">
              Nom du couple (optionnel)
            </label>
            <input
              data-testid="couple-name-input"
              value={coupleName}
              onChange={(e) => setCoupleName(e.target.value)}
              className="mt-2 w-full bg-white border border-[#E6E2DD] focus:border-[#C86B5E] focus:outline-none rounded-xl px-4 py-3.5"
              placeholder="Marie & Jean"
            />
          </div>
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-[#5C5754] font-semibold">
              {stage === "married" ? "Date de votre mariage (optionnel)" : "Date prévue du mariage (optionnel)"}
            </label>
            <input
              data-testid="wedding-date-input"
              type="date"
              value={weddingDate}
              onChange={(e) => setWeddingDate(e.target.value)}
              className="mt-2 w-full bg-white border border-[#E6E2DD] focus:border-[#C86B5E] focus:outline-none rounded-xl px-4 py-3.5"
            />
          </div>
          <button
            data-testid="create-couple-button"
            type="submit"
            disabled={loading}
            className="bg-[#C86B5E] hover:bg-[#B35A4E] disabled:opacity-50 text-white rounded-full px-10 py-4 transition-colors"
          >
            {loading ? "Création…" : "Créer notre couple"}
          </button>
        </form>
      ) : (
        <form onSubmit={handleJoin} className="space-y-6">
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-[#5C5754] font-semibold">
              Code d'invitation
            </label>
            <input
              data-testid="invite-code-input"
              required
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="mt-2 w-full bg-white border border-[#E6E2DD] focus:border-[#C86B5E] focus:outline-none rounded-xl px-4 py-4 text-2xl tracking-[0.15em] text-center font-heading"
              placeholder="XXXXXX"
              maxLength={8}
            />
          </div>
          <button
            data-testid="join-couple-button"
            type="submit"
            disabled={loading}
            className="bg-[#C86B5E] hover:bg-[#B35A4E] disabled:opacity-50 text-white rounded-full px-10 py-4 transition-colors"
          >
            {loading ? "Rejoindre…" : "Rejoindre mon partenaire"}
          </button>
        </form>
      )}
    </div>
  );
}
